import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Check, Mail, User, Lock, Shield, ArrowRight, ArrowLeft } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialMode?: 'login' | 'signup';
}

interface PasswordStrength {
  score: number;
  message: string;
  color: string;
}

type SignUpStep = 'email' | 'password' | 'profile';

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialMode = 'login'
}) => {
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup');
  const [signUpStep, setSignUpStep] = useState<SignUpStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [username, setUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showVerificationMessage, setShowVerificationMessage] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    message: '',
    color: 'bg-gray-200'
  });

  const { signIn, signUp, resetPassword, signInWithGoogle } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setIsSignUp(initialMode === 'signup');
      setSignUpStep('email');
    }
  }, [isOpen, initialMode]);

  const checkPasswordStrength = (pass: string): PasswordStrength => {
    if (!pass) return { score: 0, message: '', color: 'bg-gray-200' };

    let score = 0;
    if (pass.length >= 6) score++;
    if (pass.length >= 8) score++;
    if (/[A-Z]/.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^A-Za-z0-9]/.test(pass)) score++;

    const strengthMap: Record<number, PasswordStrength> = {
      0: { score: 0, message: 'Вельмі слабы', color: 'bg-red-500' },
      1: { score: 1, message: 'Слабы', color: 'bg-orange-500' },
      2: { score: 2, message: 'Сярэдні', color: 'bg-yellow-500' },
      3: { score: 3, message: 'Добры', color: 'bg-blue-500' },
      4: { score: 4, message: 'Моцны', color: 'bg-green-500' },
      5: { score: 5, message: 'Вельмі моцны', color: 'bg-green-600' }
    };

    return strengthMap[score];
  };

  useEffect(() => {
    if (isSignUp) {
      setPasswordStrength(checkPasswordStrength(password));
    }
  }, [password, isSignUp]);

  const validatePassword = (pass: string): { isValid: boolean; message: string } => {
    if (pass.length < 6) {
      return { 
        isValid: false, 
        message: 'Пароль павінен змяшчаць не менш за 6 сімвалаў' 
      };
    }
    return { isValid: true, message: '' };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      if (isResetPassword) {
        const { error } = await resetPassword(email);
        if (error) throw error;
        toast.success('Інструкцыі па скідванні пароля адпраўлены на вашу пошту');
        onClose();
      } else if (isSignUp) {
        // Пошаговая валидация
        if (signUpStep === 'email') {
          if (!email.trim()) {
            throw new Error('Калі ласка, увядзіце email');
          }
          setSignUpStep('password');
          setIsLoading(false);
          return;
        }
        
        if (signUpStep === 'password') {
          const passwordValidation = validatePassword(password);
          if (!passwordValidation.isValid) {
            throw new Error(passwordValidation.message);
          }
          if (password !== confirmPassword) {
            throw new Error('Паролі не супадаюць');
          }
          setSignUpStep('profile');
          setIsLoading(false);
          return;
        }

        if (signUpStep === 'profile') {
          if (!username.trim()) {
            throw new Error('Калі ласка, увядзіце імя карыстальніка');
          }
          if (!agreeToTerms) {
            throw new Error('Калі ласка, пагадзіцеся з умовамі');
          }

          const { error } = await signUp(email, password, username);
          
          if (error) {
            if (error.message === 'User already registered') {
              throw new Error('Карыстальнік з такім email ужо зарэгістраваны');
            } else if (error.message === 'Error sending confirmation email') {
              throw new Error('Не атрымалася адправіць ліст пацверджання. Калі ласка, паспрабуйце пазней');
            } else {
              throw error;
            }
          }

          setShowVerificationMessage(true);
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          if (error.message === 'Invalid login credentials') {
            throw new Error('Няправільны email або пароль');
          } else {
            throw error;
          }
        }
        
        onClose();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Адбылася памылка');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      const { error } = await signInWithGoogle();
      if (error) throw error;
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Памылка ўваходу праз Google');
    }
  };

  const handleClose = () => {
    // Сброс состояния при закрытии
    setIsSignUp(false);
    setSignUpStep('email');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setUsername('');
    setAgreeToTerms(false);
    setIsResetPassword(false);
    setShowVerificationMessage(false);
    onClose();
  };

  const handleBackStep = () => {
    if (signUpStep === 'password') {
      setSignUpStep('email');
    } else if (signUpStep === 'profile') {
      setSignUpStep('password');
    }
  };

  const getStepTitle = () => {
    if (isResetPassword) return 'Скід пароля';
    if (!isSignUp) return 'Уваход';
    
    switch (signUpStep) {
      case 'email': return 'Рэгістрацыя';
      case 'password': return 'Стварыце пароль';
      case 'profile': return 'Завяршэнне';
      default: return 'Рэгістрацыя';
    }
  };

  const getStepDescription = () => {
    if (isResetPassword) return 'Увядзіце ваш email';
    if (!isSignUp) return 'Увайдзіце ў ваш акаўнт';
    
    switch (signUpStep) {
      case 'email': return 'Увядзіце ваш email адрас';
      case 'password': return 'Прыдумайце надзейны пароль';
      case 'profile': return 'Апошні крок';
      default: return 'Стварыце новы акаўнт';
    }
  };

  const getStepIcon = () => {
    if (isResetPassword) return Shield;
    if (!isSignUp) return Lock;
    
    switch (signUpStep) {
      case 'email': return Mail;
      case 'password': return Lock;
      case 'profile': return User;
      default: return User;
    }
  };

  if (!isOpen) return null;

  if (showVerificationMessage) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl w-full max-w-md relative overflow-hidden shadow-2xl">
          <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-6 border-b border-gray-100 relative">
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
            <div className="text-center">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Пацвердзіце ваш email</h2>
            </div>
          </div>

          <div className="p-6">
            <p className="text-gray-600 mb-6 leading-relaxed">
              Мы адправілі ліст з спасылкай для пацверджання на адрас <strong>{email}</strong>. 
              Калі ласка, праверце вашу пошту і перайдзіце па спасылцы для завяршэння рэгістрацыі.
            </p>
            <button
              onClick={handleClose}
              className="w-full bg-primary text-white py-3 rounded-xl hover:bg-primary/90 transition-all duration-300 font-medium"
            >
              Зразумела
            </button>
          </div>
        </div>
      </div>
    );
  }

  const StepIcon = getStepIcon();

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-md relative overflow-hidden shadow-2xl">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-primary/5 to-primary/10 p-6 border-b border-gray-100 relative">
          <button
            onClick={handleClose}
            className="absolute right-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-600" />
          </button>
          
          {/* Back Button для пошаговой регистрации */}
          {isSignUp && signUpStep !== 'email' && (
            <button
              onClick={handleBackStep}
              className="absolute left-4 top-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <ArrowLeft size={20} className="text-gray-600" />
            </button>
          )}
          
          <div className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <StepIcon className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">{getStepTitle()}</h2>
            <p className="text-gray-600 mt-1">{getStepDescription()}</p>
            
            {/* Progress Indicator для регистрации */}
            {isSignUp && (
              <div className="flex justify-center mt-4 space-x-2">
                <div className={`w-2 h-2 rounded-full transition-colors ${signUpStep === 'email' ? 'bg-primary' : 'bg-gray-300'}`} />
                <div className={`w-2 h-2 rounded-full transition-colors ${signUpStep === 'password' ? 'bg-primary' : 'bg-gray-300'}`} />
                <div className={`w-2 h-2 rounded-full transition-colors ${signUpStep === 'profile' ? 'bg-primary' : 'bg-gray-300'}`} />
              </div>
            )}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Google Sign In - только на первом шаге */}
          {!isResetPassword && (!isSignUp || signUpStep === 'email') && (
            <>
              <button
                type="button"
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 border-2 border-gray-200 rounded-xl py-3 px-4 hover:border-primary hover:bg-primary/5 transition-all duration-300 group"
              >
                <img src="https://www.google.com/favicon.ico" alt="Google" className="w-5 h-5" />
                <span className="font-medium text-gray-700 group-hover:text-primary">
                  Працягнуць праз Google
                </span>
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500 font-medium">альбо</span>
                </div>
              </div>
            </>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            
            {/* Email Step */}
            {(!isSignUp || signUpStep === 'email') && (
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-700">
                  Email адрас
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white"
                    placeholder="example@email.com"
                    required
                  />
                </div>
              </div>
            )}

            {/* Password Step */}
            {!isResetPassword && (!isSignUp || signUpStep === 'password') && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Пароль
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white"
                      placeholder="Увядзіце пароль"
                      required
                      minLength={6}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                  
                  {/* Password Strength */}
                  {isSignUp && password && (
                    <div className="space-y-2">
                      <div className="h-2 rounded-full bg-gray-200 overflow-hidden">
                        <div 
                          className={`h-full ${passwordStrength.color} transition-all duration-300`}
                          style={{ width: `${(passwordStrength.score / 5) * 100}%` }}
                        />
                      </div>
                      <p className={`text-sm font-medium ${passwordStrength.color.replace('bg-', 'text-')}`}>
                        {passwordStrength.message}
                      </p>
                    </div>
                  )}
                </div>

                {/* Confirm Password для регистрации */}
                {isSignUp && (
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Пацвердзіце пароль
                    </label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type={showConfirmPassword ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-11 pr-12 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white"
                        placeholder="Паўтарыце пароль"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                    {confirmPassword && password === confirmPassword && (
                      <div className="flex items-center gap-2 text-green-600 text-sm">
                        <Check size={16} />
                        <span className="font-medium">Паролі супадаюць</span>
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Profile Step */}
            {isSignUp && signUpStep === 'profile' && (
              <>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Імя карыстальніка
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 bg-gray-50 focus:bg-white"
                      placeholder="Увядзіце ваша імя"
                      required
                    />
                  </div>
                </div>

                {/* Terms Agreement */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-xl">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                  />
                  <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer select-none leading-relaxed">
                    Я згодны з{' '}
                    <Link 
                      to="/privacy-policy" 
                      target="_blank" 
                      className="text-primary hover:underline font-medium"
                      onClick={(e) => e.stopPropagation()}
                    >
                      палітыкай апрацоўкі персанальных дадзеных
                    </Link>
                  </label>
                </div>
              </>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-primary text-white py-3 rounded-xl hover:bg-primary/90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed font-semibold flex items-center justify-center gap-2 group"
              disabled={isLoading || (isSignUp && signUpStep === 'profile' && !agreeToTerms)}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Загрузка...</span>
                </>
              ) : (
                <>
                  <span>
                    {isResetPassword 
                      ? 'Адправіць' 
                      : isSignUp 
                        ? signUpStep === 'profile' 
                          ? 'Стварыць акаўнт' 
                          : 'Далей'
                        : 'Увайсці'
                    }
                  </span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </button>

            {/* Navigation Links */}
            <div className="space-y-3 pt-2">
              {!isResetPassword && (
                <button
                  type="button"
                  onClick={() => {
                    setIsSignUp(!isSignUp);
                    setSignUpStep('email');
                    setPassword('');
                    setConfirmPassword('');
                    setUsername('');
                    setAgreeToTerms(false);
                  }}
                  className="w-full text-center text-sm text-gray-600 hover:text-primary transition-colors font-medium"
                >
                  {isSignUp
                    ? 'Ужо ёсць акаўнт? Увайсці'
                    : 'Няма акаўнта? Зарэгістравацца'}
                </button>
              )}

              <button
                type="button"
                onClick={() => {
                  setIsResetPassword(!isResetPassword);
                  setSignUpStep('email');
                  setPassword('');
                  setConfirmPassword('');
                  setUsername('');
                  setAgreeToTerms(false);
                }}
                className="w-full text-center text-sm text-gray-600 hover:text-primary transition-colors font-medium"
              >
                {isResetPassword
                  ? 'Вярнуцца да ўваходу'
                  : 'Забылі пароль?'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};